/**
 * El confeti, sin dibujo: cómo nace cada papelito y cómo cae.
 *
 * Aparte del lienzo para poder fijarlo sin navegador, y sin dependencia nueva:
 * es gravedad, un poco de aire y un giro. Los colores llegan de fuera —los
 * tokens `--blog-confetti-*` y `--blog-tile-*`— porque esta parte no sabe de CSS.
 */

export interface Particle {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly rotation: number;
  readonly spin: number;
  readonly width: number;
  readonly height: number;
  readonly color: string;
  /** Fase del vaivén, que es lo que hace que un papel no caiga como piedra. */
  readonly wobble: number;
  /** Segundos que le quedan. Un tope, por si una ventana enorme no lo ve salir. */
  readonly life: number;
}

/** px/s². Más que la real a esta escala: si no, parece que flota. */
const GRAVITY = 1100;
/** Cuánto le quita el aire a la velocidad, por segundo. */
const DRAG = 0.9;
const MAX_LIFE_S = 5;
const SIZE_MIN = 6;
const SIZE_SPREAD = 6;

const between = (random: () => number, min: number, max: number): number =>
  min + random() * (max - min);

/**
 * Dos cañones, uno en cada esquina de abajo, disparando hacia el centro.
 *
 * Desde abajo y no lloviendo desde arriba porque así se lee como celebración —
 * el «¡sorpresa!» de un cañón— y no como nieve. La mitad sale de cada lado.
 */
export const spawnBurst = (params: {
  readonly count: number;
  readonly width: number;
  readonly height: number;
  readonly colors: readonly string[];
  readonly random?: () => number;
}): Particle[] => {
  const random = params.random ?? Math.random;
  const { width, height, colors } = params;
  if (colors.length === 0) return [];

  return Array.from({ length: params.count }, (_, index): Particle => {
    const fromLeft = index % 2 === 0;
    const size = SIZE_MIN + random() * SIZE_SPREAD;
    return {
      x: fromLeft ? between(random, 0, width * 0.08) : between(random, width * 0.92, width),
      y: height + between(random, 0, 20),
      vx: (fromLeft ? 1 : -1) * between(random, width * 0.15, width * 0.55),
      vy: -between(random, height * 0.9, height * 1.6),
      rotation: random() * Math.PI * 2,
      spin: between(random, -12, 12),
      width: size,
      height: size * between(random, 0.4, 0.8),
      color: colors[index % colors.length] ?? colors[0] ?? '',
      wobble: random() * Math.PI * 2,
      life: MAX_LIFE_S,
    };
  });
};

/** Un paso de `dt` segundos. */
export const stepParticle = (p: Particle, dt: number): Particle => {
  const drag = Math.pow(DRAG, dt * 10);
  return {
    ...p,
    x: p.x + (p.vx + Math.sin(p.wobble) * 40) * dt,
    y: p.y + p.vy * dt,
    vx: p.vx * drag,
    vy: p.vy * drag + GRAVITY * dt,
    rotation: p.rotation + p.spin * dt,
    wobble: p.wobble + dt * 8,
    life: p.life - dt,
  };
};

/** Ya cayó por debajo de la ventana, o se le acabó el tiempo. */
export const isGone = (p: Particle, height: number): boolean =>
  p.life <= 0 || (p.vy > 0 && p.y > height + p.width * 2);
